import React, { Component, ErrorInfo } from 'react';
import { useLocation } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Optional fallback UI. If not provided, a generic error card is shown. */
  fallback?: React.ReactNode;
  /**
   * When any value in this array changes, a latched error is cleared so the
   * boundary re-renders its children. Pass the current route (e.g.
   * `[location.pathname]`) so navigating away — including the browser Back
   * button — escapes the error screen instead of leaving the user stuck on it.
   */
  resetKeys?: ReadonlyArray<unknown>;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * ErrorBoundary catches uncaught React render errors and displays a
 * graceful fallback instead of a blank screen.
 *
 * Usage — wrap the entire app (outer) and individual route panels (inner):
 *
 *   <ErrorBoundary>          ← outer — catches catastrophic failures
 *     <Routes>
 *       <Route path="/" element={
 *         <ErrorBoundary fallback={<p>Page error — try refreshing.</p>}>
 *           <MyPage />
 *         </ErrorBoundary>
 *       } />
 *     </Routes>
 *   </ErrorBoundary>
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Log to console — in production this can be forwarded to a monitoring service
    console.error('[ErrorBoundary] Uncaught render error:', error, info.componentStack);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    // Once an error is latched, React never resets it on its own. Clear it when
    // resetKeys change (e.g. the route changed) so navigation / Back recovers.
    if (this.state.hasError && this.resetKeysChanged(prevProps.resetKeys, this.props.resetKeys)) {
      this.setState({ hasError: false, error: null });
    }
  }

  private resetKeysChanged(
    prev: ReadonlyArray<unknown> | undefined,
    next: ReadonlyArray<unknown> | undefined,
  ): boolean {
    if (prev === next) return false;
    if (!prev || !next || prev.length !== next.length) return true;
    return prev.some((value, i) => !Object.is(value, next[i]));
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return <>{this.props.fallback}</>;
      }

      return (
        <div
          className="flex flex-col items-center justify-center min-h-[200px] p-8 text-center"
          data-testid="error-boundary-fallback"
          role="alert"
        >
          <AlertCircle className="w-12 h-12 text-qc-error mb-4" />
          <h2 className="text-xl font-semibold text-text-primary mb-2">
            Something went wrong
          </h2>
          <p className="text-sm text-text-tertiary mb-6 max-w-md">
            An unexpected error occurred. Refresh the page or click the button below to try again.
          </p>
          <button
            onClick={this.handleReset}
            className="px-4 py-2 bg-interactive-primary hover:bg-interactive-primary-hover text-text-inverse text-sm font-medium rounded-lg transition-colors"
            data-testid="error-boundary-reset"
          >
            Try again
          </button>
        </div>
      );
    }

    return <>{this.props.children}</>;
  }
}

/**
 * Router-aware ErrorBoundary. Resets automatically whenever the current route
 * changes, so a render error on one page never traps the user — navigating
 * away, or pressing the browser Back button, clears the error and shows the
 * destination page. Must be rendered INSIDE a Router (it uses `useLocation`).
 */
export function RouteResetErrorBoundary({
  children,
  fallback,
}: Pick<ErrorBoundaryProps, 'children' | 'fallback'>): React.ReactElement {
  const location = useLocation();
  return (
    <ErrorBoundary resetKeys={[location.pathname]} fallback={fallback}>
      {children}
    </ErrorBoundary>
  );
}
